import '../index.scss';
import { useEffect, useRef, useState } from 'react';
import UKLoadingIcon, { CIRCLE_COLOR_MAP } from '@/components/UKLoadingIcon';
import UKPageNothing from '@/components/UKPageNothing';
import UKPageLoading from '@/components/UKPageLoading';
import { CommonItem, CampaignItem, JumpBtnItem, PriceDropItem } from './messageItem';
import UKPageError from '@/components/UKPageError';
import to from 'await-to-js';
import UKToast from '@/components/UKToast/index';
import { useTranslation } from '@/i18n/client';
import { getUserInfoApi } from '@/apis/login/login.api';
import { getMsgListApi, handleBatchDeleteApi } from '@/apis/message/message.api';
import { MessageItem, MessageSubTypeEnum, MessageTypeEnum } from '@/apis/message/message.interface';
import '../index.scss';
import {
  MESSAGE_EVENT_NAMES,
  messageBusinessLogger,
} from '@/utils/logger/instance/messageBusinessLogger';

export default function MessageList({
  type,
  title,
  onClose,
  onModify,
}: {
  type: number;
  title: string;
  onClose: () => void;
  onModify: () => void;
}) {
  const threshold = 1;
  const { t } = useTranslation();

  const [isMsgLoading, setIsMsgLoading] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight + threshold >= target.scrollHeight) {
      handleMessageLoading();
    }
  };

  const handleMessageLoading = async () => {
    if (currentPage >= totalPage || isMsgLoading) {
      return;
    }
    setIsMsgLoading(true);
    await fetchMsgList(true);
    setIsMsgLoading(false);
  };

  const scrollToBottom = () => {
    if (divRef.current && divRef.current.parentElement) {
      divRef.current.parentElement.scrollTop = divRef.current.parentElement.scrollHeight;
    }
  };

  const pageSize = 10;
  const [userInfo, setUserInfo] = useState<any>(null);
  const [error, setError] = useState<boolean>(false);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [data, setData] = useState<MessageItem[]>([]);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);

  const initMessage = async () => {
    setIsPageLoading(true);
    await Promise.all([fetchMsgList(), getUserInfo()]);
    setIsPageLoading(false);
  };

  const onDeleteInitMessage = async () => {
    setIsPageLoading(true);
    await fetchMsgList();
    setIsPageLoading(false);
  };

  const getUserInfo = async () => {
    const [_, res] = await getUserInfoApi();
    if (res && res.success) {
      setUserInfo(res.data);
    }
  };

  const fetchMsgList = async (isLoading: boolean = false) => {
    const curPage = isLoading ? currentPage + 1 : 1;
    const [error, res] = await to(
      getMsgListApi({
        accountType: type,
        pageSize: pageSize,
        currentPage: curPage,
      }),
    );
    if (error) {
      setError(true);
      setData([]);
      // 报错需上报错误日志
      messageBusinessLogger({
        eventName: MESSAGE_EVENT_NAMES.MESSAGE_DETAIL_PAGE_LOAD_FAIL,
        message: {
          type: type,
          isError: true,
        },
      });
    } else if (res && res.code === '200') {
      const { data: dataList = [], total } = res.data || {};
      const msgData = isLoading ? [...data, ...dataList] : dataList;
      setError(false);
      setData(msgData);
      setCurrentPage(curPage);
      setTotalPage((total || 0) / pageSize);
    } else {
      UKToast.show({
        content: res?.msg || t('common.message.backLater'),
        icon: 'warning',
      });
    }
  };

  const handleMessageDelete = (index: number, msgId: string) => {
    if (index == 0) {
      onModify();
    }
    onMessageDelete(msgId);
  };

  const onMessageDelete = async (msgId: string) => {
    const res = await handleBatchDeleteApi({ msgId });
    if (res) {
      UKToast.show({
        content: t('common.message.deleteSuccessful'),
        icon: 'success',
      });
      setData([]);
      onDeleteInitMessage();
    } else {
      UKToast.show({
        content: t('common.message.systemError'),
        icon: 'warning',
      });
    }
  };

  useEffect(() => {
    initMessage();
  }, [type]);

  useEffect(() => {
    if (isMsgLoading) {
      scrollToBottom();
    }
  }, [isMsgLoading]);

  if (error) {
    return (
      <UKPageError
        style={{ marginTop: '180px' }}
        clickTryAgain={() => initMessage()}
        text={t('common.pageStatus.error')}
        btnText={t('common.btnText.tryAgain')}
      />
    );
  }

  if (isPageLoading) {
    return <UKPageLoading style={{ height: '100%' }} />;
  }

  if (!data || !data?.length) {
    return <UKPageNothing style={{ marginTop: '250px' }} />;
  }

  return (
    <div ref={divRef} className='message-list' onScroll={handleScroll}>
      {data?.map((item: MessageItem, index: number) => {
        const params = {
          msgType: title,
          msgId: item?.msgId || '',
          orderId: String(item?.extraAttribute?.orderId) || '',
          promId: item?.extraAttribute?.promoId || '',
          pos: index,
        };
        const data = {
          item: item,
          type: type,
          params: params,
          userInfo: userInfo,
          onClose: onClose,
          onDelete: (msgId: string) => {
            handleMessageDelete(index, msgId);
          },
        };
        const msgType = item?.extraAttribute?.msgSubType;
        if (msgType) {
          switch (msgType) {
            case MessageSubTypeEnum.CAMPAIGN:
              return <CampaignItem key={index} data={data} />;
            case MessageSubTypeEnum.PRICE_DROP:
              return <PriceDropItem key={index} data={data} />;
            case MessageSubTypeEnum.EMAIL:
              return <JumpBtnItem key={index} data={data} />;
            default:
              return <CommonItem key={index} data={data} />;
          }
        } else {
          switch (type) {
            case MessageTypeEnum.campaign:
              return <CampaignItem key={index} data={data} />;
            case MessageTypeEnum.pricedrop:
              return <PriceDropItem key={index} data={data} />;
            default:
              return <CommonItem key={index} data={data} />;
          }
        }
      })}
      {isMsgLoading && (
        <div className='loading'>
          <UKLoadingIcon className='loading-icon' type={CIRCLE_COLOR_MAP.BLACK} />
        </div>
      )}
      {currentPage >= totalPage && data && data.length >= pageSize && (
        <div className='loading reach-bottom'>{t('common.message.end')}</div>
      )}
    </div>
  );
}
